import { MedProFilter, MedProFilterFieldKey } from './medpro.models';

export type FilterValueInputType = 'text' | 'number' | 'statePicker' | 'designationPicker';

export interface FilterFieldDef {
  key: MedProFilterFieldKey;
  translationKey: string;
  /** Dataverse virtual entity attribute name. */
  attribute: string;
  inputType: FilterValueInputType;
  uppercase?: boolean;
  /** Placeholder text shown when the value input is empty. */
  placeholder?: string;
  /** Per-MedPro-API min character count for the value (excluding wildcards). */
  minLength?: number;
  /** Per-MedPro-API exact character count (overrides minLength). */
  exactLength?: number;
  /** Per MedPro: "At least one of the following must not be empty: lastName,
   *  slnStateLicenseNumber, npiNumber, deaNumber, cdsNumber, query." Marks
   *  this field as one that satisfies the API's primary-filter requirement. */
  isPrimary?: boolean;
}

export const MEDPRO_FILTER_FIELDS: FilterFieldDef[] = [
  // Per MedPro swagger /v1/hcps/search.query: "Search by one or more terms.
  // Name, licenses, address, and professional type are all supported. If
  // included, all other HCP search filters are ignored except 'IncludeInactiveMonths'."
  { key: 'query',        translationKey: 'MEDPRO_FILTER_FREETEXT',    attribute: 'indskr_query',                     inputType: 'text',
    placeholder: 'Search by name, license, address, or professional type', isPrimary: true },
  { key: 'firstName',    translationKey: 'MEDPRO_FILTER_FIRSTNAME',   attribute: 'indskr_firstname',                 inputType: 'text',
    placeholder: 'e.g. John', minLength: 1 },
  { key: 'lastName',     translationKey: 'MEDPRO_FILTER_LASTNAME',    attribute: 'indskr_lastname',                  inputType: 'text',
    placeholder: 'e.g. Smith', minLength: 2, isPrimary: true },
  { key: 'middleName',   translationKey: 'MEDPRO_FILTER_MIDDLENAME',  attribute: 'indskr_middlename',                inputType: 'text',
    placeholder: 'e.g. A', minLength: 1 },
  // License-derived state (SLN). Displayed as "State (SLN)" to disambiguate
  // from the address state below; key stays `state` so existing iO config and
  // seeds keep binding to it.
  { key: 'state',        translationKey: 'MEDPRO_FILTER_LICENSE_STATE', attribute: 'indskr_licensestate',             inputType: 'statePicker',
    placeholder: '2-letter state, e.g. NY', exactLength: 2 },
  // Address state — the unqualified "State" (matches the rep's default mental
  // model). Maps to the VE's address-state column.
  { key: 'addressState', translationKey: 'MEDPRO_FILTER_STATE',        attribute: 'indskr_addressstate',             inputType: 'statePicker',
    placeholder: '2-letter state, e.g. NY', exactLength: 2 },
  { key: 'npi',          translationKey: 'MEDPRO_FILTER_NPI',         attribute: 'indskr_npinumber',                 inputType: 'number',
    placeholder: '10 digits', exactLength: 10, isPrimary: true },
  { key: 'dea',          translationKey: 'MEDPRO_FILTER_DEA',         attribute: 'indskr_deanumber',                 inputType: 'text',
    placeholder: 'Full DEA number', uppercase: true, isPrimary: true },
  { key: 'cds',          translationKey: 'MEDPRO_FILTER_CDS',         attribute: 'indskr_cdsnumber',                 inputType: 'text',
    placeholder: 'CDS number', minLength: 4, isPrimary: true },
  { key: 'sln',          translationKey: 'MEDPRO_FILTER_SLN',         attribute: 'indskr_licensenumber',             inputType: 'text',
    placeholder: 'State license number', minLength: 4, isPrimary: true },
  { key: 'designation',  translationKey: 'MEDPRO_FILTER_DESIGNATION', attribute: 'indskr_professionaldesignation',   inputType: 'designationPicker',
    placeholder: 'Pick a degree' },
  { key: 'addressLine1', translationKey: 'MEDPRO_FILTER_ADDRESSLINE1',attribute: 'indskr_addressline1',              inputType: 'text',
    placeholder: 'Street address', minLength: 6 },
  { key: 'city',         translationKey: 'MEDPRO_FILTER_CITY',        attribute: 'indskr_city',                      inputType: 'text',
    placeholder: 'City', minLength: 3 },
  { key: 'zip',          translationKey: 'MEDPRO_FILTER_ZIP',         attribute: 'indskr_zip',                       inputType: 'text',
    placeholder: 'Zip', minLength: 3 },
  { key: 'country',      translationKey: 'MEDPRO_FILTER_COUNTRY',     attribute: 'indskr_country',                   inputType: 'text',
    placeholder: '2-letter country, e.g. US', uppercase: true, exactLength: 2 },
];

export function getFilterFieldDef(key: MedProFilterFieldKey): FilterFieldDef {
  const def = MEDPRO_FILTER_FIELDS.find(f => f.key === key);
  if (!def) throw new Error(`Unknown MedPro filter field: ${key}`);
  return def;
}

/** Field keys that satisfy MedPro's "at least one of the following must not
 *  be empty" rule. Server returns this exact wording in a 400 response when
 *  none of these are populated. */
export const MEDPRO_PRIMARY_FIELD_KEYS: MedProFilterFieldKey[] =
  MEDPRO_FILTER_FIELDS.filter(d => d.isPrimary).map(d => d.key);

/** Returns true when the filter's value is empty OR meets the field's
 *  minLength / exactLength requirement. Empty values are valid here — they
 *  represent "no filter on this field"; the page-level "search disabled"
 *  check separately ensures at least one primary filter has a valid value. */
export function meetsLengthRequirement(filter: MedProFilter): boolean {
  const v = (filter.value ?? '').trim();
  if (!v) return true;
  const def = getFilterFieldDef(filter.field);
  if (def.exactLength != null) return v.length === def.exactLength;
  if (def.minLength != null) return v.length >= def.minLength;
  return true;
}

/** Map from contact-list search-suggestion category names (English) to
 *  MedPro filter keys. Categories not in this map are silently skipped
 *  when seeding the MedPro search panel. Match is case-insensitive against
 *  pill.categoryDisplayName (preferred), then categoryNameInEng, then
 *  categoryName (which can be the Dataverse logical name, not human-readable). */
const PILL_CATEGORY_TO_FILTER_KEY: Record<string, MedProFilterFieldKey> = {
  // Entity-level character-search pills like "Customers with Name - test"
  // come through with categoryName = "Customers" and the typed text in
  // pill.charSearchText — map them to MedPro's free-text 'query'.
  'customers': 'query',
  'customer': 'query',
  'first name': 'firstName',
  'last name': 'lastName',
  'middle name': 'middleName',
  'city': 'city',
  // Contact-list state pills come from the contact's address (stateorprovince),
  // so they seed the address state, not the license state.
  'state': 'addressState',
  'state/province': 'addressState',
  'country': 'country',
  'postal code': 'zip',
  'zip': 'zip',
  'street 1': 'addressLine1',
  'address': 'addressLine1',
  'address line 1': 'addressLine1',
  'designation': 'designation',
  'specialty': 'designation',  // closest MedPro equivalent
  'npi': 'npi',
  'npi number': 'npi',
  'dea': 'dea',
  'dea number': 'dea',
};

/** Convert a contact-list pill category label to a MedPro filter field key.
 *  Pass any of the pill's category strings (categoryDisplayName preferred);
 *  returns null if no MedPro field matches. */
export function pillCategoryToFilterKey(categoryName: string | undefined): MedProFilterFieldKey | null {
  if (!categoryName) return null;
  return PILL_CATEGORY_TO_FILTER_KEY[categoryName.trim().toLowerCase()] ?? null;
}

export const MEDPRO_DESIGNATIONS = [
  'MD','DO','PA','APRN','DEN','OPT','DC','NURSE','POD','VET',
  'PHAR','PSYD','CCH','NMD','SW','CP','AUD','SLP','HAD','RC','DN','DH'
];
